import React, { useEffect } from 'react';
import LabelSelect from 'src/modules/shared/components/atoms/inputs/label-select';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { IntegrationAddress } from 'src/api/generated/models/IntegrationAddress';

interface IntegrationPhoneSelectProps {
  phones: IntegrationAddress[];
  selectedPhoneId?: string;
  onPhoneChange: (phoneId?: string) => void;
  /** Whether a sender-less flow may select the first available phone. */
  autoSelectFirst?: boolean;
  /** Shared label placement; top keeps adjacent form controls aligned. */
  labelPosition?: 'side' | 'top';
}

function generatePhoneOptions(
  phones: IntegrationAddress[],
  unavailableSelectionLabel: string,
  selectedPhoneId?: string,
  showUnavailableSelection = false
) {
  const options = phones.map((phone) => {
    const displayText = phone.displayName
      ? `${phone.displayName}: ${phone.address}`
      : phone.address;

    return {
      content: displayText,
      value: phone.providerId,
    };
  });

  if (
    showUnavailableSelection ||
    (selectedPhoneId &&
      !phones.some((phone) => phone.providerId === selectedPhoneId))
  ) {
    return [
      {
        content: unavailableSelectionLabel,
        value: selectedPhoneId ?? '',
      },
      ...options,
    ];
  }

  return options;
}

const IntegrationPhoneSelect: React.FC<IntegrationPhoneSelectProps> = ({
  phones,
  selectedPhoneId,
  onPhoneChange,
  autoSelectFirst = true,
  labelPosition = 'side',
}) => {
  const { t } = useI18n();

  useEffect(() => {
    if (autoSelectFirst && phones.length > 0 && !selectedPhoneId) {
      onPhoneChange(phones[0].providerId);
    }
  }, [autoSelectFirst, phones, onPhoneChange, selectedPhoneId]);

  return (
    <LabelSelect
      label={t('communications.messageBox.whatsapp.accountLabel')}
      size="md"
      labelPosition={labelPosition}
      options={generatePhoneOptions(
        phones,
        t('communications.messageBox.whatsapp.selectedAccountUnavailable'),
        selectedPhoneId,
        !autoSelectFirst && !selectedPhoneId
      )}
      selectProps={{
        className: labelPosition === 'top' ? '!text-base' : undefined,
        value: selectedPhoneId ?? '',
        onChange: (event: React.ChangeEvent<HTMLSelectElement>) => {
          onPhoneChange(event.target.value || undefined);
        },
      }}
    />
  );
};

export default IntegrationPhoneSelect;
